'use client';

import { useState, useTransition, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { CardWrapper } from './card-wrapper';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RegisterSchema } from '@/schemas';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FormError, FormSuccess } from '@/components/form';
import { register, verifyRegistrationOtp } from '@/actions';
import { useTranslations } from '@/hooks/useTranslations';
import { useLocale } from '@/hooks/useLocale';
import { LoadingSpinner } from '@/components/animations/loading-spinner';

interface RegisterFormProps {
  inviteEmail?: string | null;
  inviteError?: string | null;
}

const OtpSchema = z.object({
  code: z.string().length(6, 'Enter the 6-digit code'),
});

export const RegisterForm = ({
  inviteEmail,
  inviteError,
}: RegisterFormProps) => {
  const searchParams = useSearchParams();
  const token = searchParams.get('token');
  const [error, setError] = useState<string | undefined>(inviteError || '');
  const [success, setSuccess] = useState<string | undefined>('');
  const [step, setStep] = useState<'credentials' | 'otp'>('credentials');
  const [pendingCredentials, setPendingCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const [isPending, startTransition] = useTransition();
  const locale = useLocale();

  const form = useForm<z.infer<typeof RegisterSchema>>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      name: '',
      email: inviteEmail || '',
      password: '',
      website: '',
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const otpForm = useForm<z.infer<typeof OtpSchema>>({
    resolver: zodResolver(OtpSchema),
    defaultValues: { code: '' },
    mode: 'onTouched',
  });

  useEffect(() => {
    if (inviteEmail) {
      form.setValue('email', inviteEmail);
    }
  }, [inviteEmail, form]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  const onSubmit = (values: z.infer<typeof RegisterSchema>) => {
    setError('');
    setSuccess('');

    startTransition(() => {
      register(values, token || undefined, locale).then((data) => {
        if (data.error) {
          setError(data.error);
          return;
        }

        if (data.success && data.email) {
          setPendingCredentials({
            email: data.email,
            password: values.password,
          });
          setSuccess(data.success);
          setStep('otp');
        }
      });
    });
  };

  const onOtpSubmit = (values: z.infer<typeof OtpSchema>) => {
    if (!pendingCredentials) {
      setError('Please sign up again.');
      setStep('credentials');
      return;
    }

    setError('');
    setSuccess('');

    startTransition(() => {
      verifyRegistrationOtp({
        email: pendingCredentials.email,
        password: pendingCredentials.password,
        code: values.code,
      })
        .then((data) => {
          if (data?.error) {
            setError(data.error);
          }
        })
        .catch((error) => {
          if (error?.digest?.includes('NEXT_REDIRECT')) {
            window.location.href = window.location.origin;
            return;
          }
          setError('Something went wrong!');
        });
    });
  };

  const t = useTranslations('RegisterForm');

  if (step === 'otp') {
    return (
      <CardWrapper
        title={t('title')}
        headerLabel={`We sent a 6-digit code to ${pendingCredentials?.email ?? 'your email'}. Enter it below to finish signing up.`}
        backButtonLabel="Use a different email"
        backButtonHref="/register"
      >
        <Form {...otpForm}>
          <form
            onSubmit={otpForm.handleSubmit(onOtpSubmit)}
            className="space-y-6"
          >
            <FormField
              control={otpForm.control}
              name="code"
              render={({ field }) => (
                <FormItem className="flex flex-col items-center">
                  <FormLabel>Verification code</FormLabel>

                  <FormControl>
                    <InputOTP
                      maxLength={6}
                      containerClassName="justify-center"
                      {...field}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot className="w-12 h-14 text-xl" index={0} />
                        <InputOTPSlot className="w-12 h-14 text-xl" index={1} />
                        <InputOTPSlot className="w-12 h-14 text-xl" index={2} />
                      </InputOTPGroup>

                      <InputOTPSeparator />

                      <InputOTPGroup>
                        <InputOTPSlot className="w-12 h-14 text-xl" index={3} />
                        <InputOTPSlot className="w-12 h-14 text-xl" index={4} />
                        <InputOTPSlot className="w-12 h-14 text-xl" index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormError message={error} />
            <FormSuccess message={success} />

            <Button
              type="submit"
              className="w-full"
              disabled={isPending}
              aria-label="Verify code"
            >
              {isPending ? <LoadingSpinner /> : 'Verify and continue'}
            </Button>
          </form>
        </Form>
      </CardWrapper>
    );
  }

  return (
    <CardWrapper
      title={t('title')}
      headerLabel={t('headerLabel')}
      backButtonLabel={t('backButtonLabel')}
      backButtonHref="/login"
      showSocial
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('nameLabel')}</FormLabel>

                  <FormControl>
                    <Input
                      {...field}
                      disabled={isPending}
                      placeholder={t('namePlaceholder') || 'John Doe'}
                      className={
                        fieldState.error
                          ? 'border-red-600 focus-visible:ring-red-600'
                          : ''
                      }
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('emailLabel')}</FormLabel>

                  <FormControl>
                    <Input
                      {...field}
                      disabled={isPending || !!inviteEmail}
                      placeholder="john.doe@example.com"
                      type="email"
                      className={
                        fieldState.error
                          ? 'border-red-600 focus-visible:ring-red-600'
                          : ''
                      }
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('passwordLabel')}</FormLabel>

                  <FormControl>
                    <Input
                      {...field}
                      disabled={isPending}
                      placeholder="********"
                      type="password"
                      className={
                        fieldState.error
                          ? 'border-red-600 focus-visible:ring-red-600'
                          : ''
                      }
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Honeypot — hidden from real users, bots auto-fill it */}
          <div
            className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10"
            aria-hidden="true"
            tabIndex={-1}
          >
            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website</FormLabel>
                  <FormControl>
                    <Input {...field} tabIndex={-1} autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <FormError message={error} />
          <FormSuccess message={success} />

          <Button
            type="submit"
            className="w-full"
            disabled={isPending}
            aria-label={t('registerButton')}
          >
            {isPending ? <LoadingSpinner /> : t('registerButton')}
          </Button>
        </form>
      </Form>
    </CardWrapper>
  );
};
